/**
 * chat-message-dispatcher.ts
 * 从 chat.ts 提取的消息解析与分发逻辑
 * 包括：群聊消息解析、多条消息逐条发送、单条消息处理、空响应兜底
 */

import type { ChatAssistantMessage, SimulatedImage } from '../types/chat'
import type { ParsedAIResponse } from './chat-post-processors'

import { toRaw } from 'vue'

import { createTextStream, estimateStreamDuration, getRandomDelay, sleep } from '../utils/gradual-message'
import { applyCallModePrefix, buildEmotionTag } from './chat-helpers'

/**
 * 将响应式对象转为可结构化克隆的纯对象（用于 BroadcastChannel.postMessage）
 * toRaw 只去顶层 Proxy，JSON 往返可深度脱代理并自动剥离不可序列化字段（如 ReadableStream）
 */
function toBroadcastSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(toRaw(value)))
}

/**
 * 在目标消息列表里按对象身份（而非 timestamp）重新定位 push 进去的消息。
 *
 * push 后 Vue 会用 reactive Proxy 包裹原对象，`list.find(m => m === message)` 直接比较会失败，
 * 故用 toRaw 脱代理后比身份。比按 timestamp 查找更可靠：timestamp 由多条/单条两条路径
 * 各自用 Date.now()/messageTimestamp++ 独立铸造，理论上可能撞车，对象身份则天然唯一。
 *
 * @returns 列表中的响应式引用；找不到（已被切换会话/删除）时返回 undefined
 */
function findReactiveMessage(message: object, ctx: MessageDispatchContext): any | undefined {
  const raw = toRaw(message)
  return ctx.getTargetMessages().find((m: any) => toRaw(m) === raw)
}

/**
 * 给消息挂上逐字流，push 后等流真正播放完毕再清除 textStream（切回 Markdown 静态渲染）。
 *
 * 相比旧实现的改进：
 * - 用 createTextStream 返回的 done（流真正 close 或被 cancel 后 resolve）驱动清除，
 *   而非 `length * 30` 的墙钟估算，消除「字没蹦完就整段跳出」的时序 bug。
 * - 兜底超时：若 PoppinText 始终未挂载 reader（如消息在视口外、stream 的 pull 从不触发），
 *   done 不会 resolve，用 estimateStreamDuration 的两倍作为上限避免永久挂起。
 * - 按对象身份重新定位消息后再清除，而非取数组末尾，避免等待期间数组变化清错对象。
 *
 * @param message 已构造好（含 textStream）但尚未 push 的消息
 * @param done createTextStream 返回的 done（流播放完毕/被取消后 resolve）；无流时传 undefined
 * @param text 用于估算兜底时长的文本（与 textStream 内容一致）
 * @param ctx 消息分发上下文
 * @param waitForDone true 则 await 播放完成（多条消息串行场景）；false 则 fire-and-forget（单条场景）
 */
async function pushMessageWithTextStream(
  message: ChatAssistantMessage,
  done: Promise<void> | undefined,
  text: string,
  ctx: MessageDispatchContext,
  waitForDone: boolean,
): Promise<void> {
  ctx.pushMessageToTarget(message)

  const clearStream = () => {
    // 按对象身份重新定位，避免等待期间数组变动（新消息/切会话/删除）清错对象
    const target = findReactiveMessage(message, ctx)
    if (target && target.textStream) {
      target.textStream = undefined
    }
  }

  // 兜底超时：done 永不 resolve 时（reader 未挂载）用估算时长的两倍上限，避免永久挂起
  const fallbackMs = estimateStreamDuration(text) * 2 + 500
  // done 存在则它与兜底超时竞速；done 不存在则纯靠兜底超时
  const waitPlayback = (done
    ? Promise.race([done, sleep(fallbackMs)])
    : sleep(fallbackMs)
  ).then(clearStream)

  if (waitForDone) {
    await waitPlayback
  }
  else {
    void waitPlayback
  }
}

/** 1 秒窗口内的插件系统通知会被收集成隐藏消息供 AI 感知 */
const RECENT_NOTIFICATION_WINDOW_MS = 1000
/** 多条消息之间的随机间隔区间（ms），让逐条出现更自然 */
const MULTI_MESSAGE_DELAY_MIN_MS = 1200
const MULTI_MESSAGE_DELAY_MAX_MS = 2000

/**
 * 跑插件的 onBeforeAIReply 钩子链，返回处理后的文本。
 * 钩子可改写文本、可注入系统通知（如余额不足提示）。
 * 单条与多条分支共用，消除两处复制粘贴。
 */
async function runBeforeAIReplyHooks(
  text: string,
  timestamp: number,
  ctx: MessageDispatchContext,
): Promise<string> {
  let processed = text
  for (const handler of ctx.pluginsStore.beforeAIReplyHandlers) {
    try {
      const result = await handler(processed, {
        contactId: ctx.targetContactId || ctx.contactsStore.activeContactId || '',
        timestamp,
        role: 'assistant' as const,
        messageId: String(timestamp),
      })

      if (result.modified && (result.content != null || result.message != null)) {
        processed = result.content ?? result.message ?? ''
      }

      // 处理插件返回的系统通知（如余额不足提示）
      if (result.systemNotifications && Array.isArray(result.systemNotifications)) {
        for (const notification of result.systemNotifications) {
          insertSystemNotification(notification.content, notification.icon, ctx)
        }
      }
    }
    catch (e) {
      console.error('插件 onBeforeAIReply 钩子执行失败:', e)
    }
  }
  return processed
}

/**
 * 插件处理后消息为空时（如余额不足标签被清空）的兜底：
 * 把最近 1 秒内的插件系统通知合成一条隐藏 assistant 消息，让 AI 能感知操作结果。
 * 单条与多条分支共用。
 *
 * @param timestamp 隐藏消息的时间戳
 */
function insertHiddenNotificationMessage(timestamp: number, ctx: MessageDispatchContext): void {
  const recentNotifications = ctx.getTargetMessages()
    .filter((m: any) => m.metadata?.type === 'plugin_notification' && Date.now() - m.timestamp < RECENT_NOTIFICATION_WINDOW_MS)
  if (recentNotifications.length > 0) {
    const notificationTexts = recentNotifications.map((m: any) => m.content).join('；')
    const hiddenContent = `[系统通知：${notificationTexts}]`
    const hiddenMsg: ChatAssistantMessage & { isHidden?: boolean } = {
      role: 'assistant',
      content: hiddenContent,
      slices: [{ type: 'text', text: hiddenContent }],
      tool_results: [],
      timestamp,
      isHidden: true,
    }
    ctx.pushMessageToTarget(hiddenMsg)
    console.info('🔒 插入隐藏assistant消息供AI感知:', hiddenContent)
  }
}

/**
 * 广播一条已 push 的 AI 消息到其他页面（BroadcastChannel）。
 * 按对象身份重新定位响应式引用，再 toBroadcastSafe 深度脱代理 + 剥离不可克隆字段。
 * 单条与多条分支共用。
 *
 * @param message push 时的原始消息对象（用于按身份定位）
 */
function broadcastAssistantMessage(message: ChatAssistantMessage, ctx: MessageDispatchContext): void {
  const reactiveRef = findReactiveMessage(message, ctx) ?? message
  ctx.broadcastMessage({
    type: 'new_message',
    data: {
      message: toBroadcastSafe(reactiveRef),
      contactId: ctx.targetContactId ?? ctx.contactsStore.activeContact?.id,
    },
  })
}

// ============================================================
// 类型定义
// ============================================================

/** 解析后的单条消息 */
export interface ParsedMessageItem {
  senderId?: string
  text?: string
  content?: string
  type?: 'sticker' | 'reply' | 'voice_message' | string
  name?: string
  voice?: boolean
  stickerName?: string
  replyTo?: string
  replyContent?: string
  emotion?: string
  /** 模拟图片消息；数组长度由 send_images 在工具层限制为 1-9。 */
  simulatedImages?: SimulatedImage[]
  /** 兼容旧 JSON/插件字段：单张图片描述。 */
  imageDescription?: string
}

/** 消息分发所需的上下文 */
export interface MessageDispatchContext {
  messages: { value: any[] }
  streamingMessage: { value: ChatAssistantMessage }
  notifyMessagesChanged: () => void
  contactsStore: any
  callStore: any
  pluginsStore: any
  isGroupChat: boolean
  characterName: string
  options: { skipUserMessage?: boolean }
  // hooks
  onTokenSpecialHooks: { value: Array<(tag: string) => Promise<void>> }
  // UI helpers
  setAiTypingState: (state: boolean) => void
  broadcastMessage: (msg: any) => void
  sendDesktopNotification: (msgInfo: any, opts: any) => void
  // 🔧 固定联系人消息操作（防止切换联系人后消息推到错误的列表）
  /** 发送时捕获的联系人 ID */
  targetContactId: string | null
  /** 向发送时的目标联系人消息列表追加消息（不受联系人切换影响） */
  pushMessageToTarget: (msg: any) => void
  /** 获取目标联系人的消息列表 */
  getTargetMessages: () => any[]
}

function normalizeSimulatedImages(value: unknown): SimulatedImage[] {
  if (!Array.isArray(value))
    return []
  return value
    .filter((image: any) => image && typeof image === 'object' && !('url' in image) && !('image_url' in image) && typeof image.description === 'string' && image.description.trim() && image.description.trim().length <= 2000)
    .slice(0, 9)
    .map((image: any) => ({
      description: image.description.trim(),
    }))
}

// ============================================================
// 1. 群聊消息解析
// ============================================================

/**
 * 将单个 raw item 转换为标准化的消息对象
 */
function mapRawItem(item: any, includeTextFallback = false): ParsedMessageItem {
  item = item && typeof item === 'object' ? item : {}
  const baseMsg: ParsedMessageItem = {
    senderId: item.senderId,
  }

  switch (item.type) {
    case 'sticker':
      baseMsg.type = 'sticker'
      baseMsg.stickerName = item.name
      baseMsg.text = `[表情: ${item.name}]`
      break
    case 'voice_message':
      baseMsg.type = 'voice_message'
      baseMsg.text = item.text || item.content
      break
    case 'reply':
      baseMsg.type = 'reply'
      baseMsg.replyTo = item.replyTo
      baseMsg.replyContent = item.replyContent
      baseMsg.text = item.content
      break
    default:
      baseMsg.text = includeTextFallback ? (item.content || item.text) : item.content
      break
  }

  // 保留 emotion 字段
  if (item.emotion)
    baseMsg.emotion = item.emotion

  // 保留 voice 字段
  if (item.voice)
    baseMsg.voice = true

  const normalizedImages = normalizeSimulatedImages(item.simulatedImages)
  const simulatedImages = normalizedImages.length > 0
    ? normalizedImages
    : (typeof item.imageDescription === 'string' && item.imageDescription.trim() && item.imageDescription.trim().length <= 2000
        ? [{ description: item.imageDescription.trim() }]
        : [])
  if (simulatedImages.length > 0)
    baseMsg.simulatedImages = simulatedImages

  return baseMsg
}

/**
 * 从 parsed 结果和 fullText 中解析出 messagesArray
 * 返回空数组表示不是多条消息模式
 */
export function parseMessagesArray(parsed: ParsedAIResponse, fullText: string): ParsedMessageItem[] {
  // 优先使用 processAIResponse 的结果
  if (parsed.messages && parsed.messages.length > 0 && parsed.messages[0] && typeof parsed.messages[0] === 'object' && parsed.messages[0].senderId) {
    console.info(`🎭 检测到群聊格式（来自parsed），共 ${parsed.messages.length} 个角色回复`)
    return parsed.messages.map((item: any) => mapRawItem(item, true))
  }

  // 备用方案：直接解析原始响应
  let rawParsed: any = null
  try {
    const jsonMatch = fullText.match(/\{[\s\S]*\}|\[[\s\S]*\]/)
    if (jsonMatch) {
      rawParsed = JSON.parse(jsonMatch[0])
    }
  }
  catch {
    // 解析失败，使用 parsed.messages 或 parsed.text
  }

  if (Array.isArray(rawParsed) && rawParsed.length > 0 && rawParsed[0] && typeof rawParsed[0] === 'object' && rawParsed[0].senderId) {
    console.info(`🎭 检测到群聊格式（来自rawParsed），共 ${rawParsed.length} 个角色回复`)
    return rawParsed.map((item: any) => mapRawItem(item, false))
  }

  // 普通多条消息格式
  if (parsed.messages && parsed.messages.length > 0) {
    return parsed.messages
  }

  return []
}

// ============================================================
// 辅助：插入系统通知消息（类似通话记录时间戳样式）
// ============================================================

/**
 * 插入一条系统通知消息到聊天列表
 * 样式与通话结束记录一致（居中圆角小灰条）
 */
/** insertSystemNotification 所需的最小上下文 */
export type SystemNotificationContext = Pick<MessageDispatchContext, 'messages' | 'notifyMessagesChanged' | 'pushMessageToTarget'>

export function insertSystemNotification(
  content: string,
  icon: string | undefined,
  ctx: SystemNotificationContext,
  details?: string,
): void {
  const systemMsg: any = {
    role: 'system',
    content,
    timestamp: Date.now(),
    metadata: {
      type: 'plugin_notification',
      icon: icon || 'i-solar:wallet-bold-duotone',
      ...(details ? { details } : {}),
    },
  }
  // 🔧 使用固定联系人操作（如果可用），防止切换联系人后推到错误列表
  if (ctx.pushMessageToTarget) {
    ctx.pushMessageToTarget(systemMsg)
  }
  else {
    ctx.messages.value.push(systemMsg)
    ctx.notifyMessagesChanged()
  }
  console.info(`📢 系统通知: ${content}`)
}

// ============================================================
// 2. 多条消息逐条发送
// ============================================================

/**
 * 逐条发送多条消息（群聊或多条回复模式）
 */
export async function dispatchMultipleMessages(
  messagesArray: ParsedMessageItem[],
  ctx: MessageDispatchContext,
  innerVoice?: string,
): Promise<void> {
  console.debug(`📨 处理多条消息，共 ${messagesArray.length} 条，开始逐条发送`)

  let messageTimestamp = Date.now()
  let hasShownFirstMessage = false

  // 💭 内心独白是整轮一段（send_messages 的顶层 inner_voice），只挂本轮「第一条」
  // 持久化消息，避免多条气泡重复展示同一段独白。
  let innerVoiceAttached = false

  for (let i = 0; i < messagesArray.length; i++) {
    const msg = messagesArray[i]
    if (!msg || typeof msg !== 'object') {
      console.warn('⚠️ 跳过格式无效的消息项')
      continue
    }
    const simulatedImages = Array.isArray(msg.simulatedImages)
      ? normalizeSimulatedImages(msg.simulatedImages)
      : (typeof msg.imageDescription === 'string' && msg.imageDescription.trim() && msg.imageDescription.trim().length <= 2000
          ? [{
              description: msg.imageDescription.trim(),
            }]
          : [])
    // 只有通过校验的图片才算图片消息；避免旧 JSON/插件只带 type 时落库为空图片气泡。
    const isSimulatedImage = simulatedImages.length > 0

    if (!isSimulatedImage && (!msg.text || !msg.text.trim())) {
      console.warn('⚠️ 跳过空消息')
      continue
    }

    // 图片描述是结构化消息，不交给文本插件改写，也不生成额外正文。
    const originalText = isSimulatedImage ? '' : (msg.text || '')
    const processedText = isSimulatedImage ? originalText : await runBeforeAIReplyHooks(originalText, messageTimestamp, ctx)
    msg.text = processedText

    // 插件处理后再次检查是否为空（如余额不足时标签被删除，剩余文字无意义）
    if (!isSimulatedImage && (!processedText || !processedText.trim())) {
      console.warn('⚠️ 插件处理后消息为空，跳过')
      insertHiddenNotificationMessage(messageTimestamp++, ctx)
      continue
    }

    // 在第一条有效消息显示前隐藏 typing 气泡
    if (!hasShownFirstMessage) {
      ctx.setAiTypingState(false)
      hasShownFirstMessage = true
    }

    // 创建消息，直接设置完整文本，用 textStream 驱动 PoppinText 流畅动画
    const textStreamHandle = processedText ? createTextStream(processedText) : undefined
    const individualMessage: ChatAssistantMessage = {
      role: 'assistant',
      content: processedText,
      slices: [{ type: 'text', text: processedText }],
      tool_results: [],
      timestamp: messageTimestamp++,
      ...(textStreamHandle ? { textStream: textStreamHandle.stream } : {}),
      ...(isSimulatedImage ? { messageType: 'simulated_image', simulatedImages } : {}),
    }

    // 💭 首条持久化消息挂上本轮内心独白（修剪后非空时）
    if (!innerVoiceAttached && innerVoice?.trim()) {
      individualMessage.innerVoice = innerVoice.trim()
      innerVoiceAttached = true
    }

    // 群聊：添加 senderId 和 senderName
    if (msg.senderId && ctx.isGroupChat) {
      individualMessage.senderId = msg.senderId
      const senderContact = ctx.contactsStore.getContact(msg.senderId)
      if (senderContact) {
        individualMessage.senderName = senderContact.name
        console.info(`🎭 群聊消息来自 ${senderContact.name} (${msg.senderId})`)
      }
    }

    // voice: true（send_messages 工具的布尔字段）→ messageType 设为 voice_message，
    // 使后处理器 handleVoiceMessageSend 能够识别并触发 TTS 生成
    if (!isSimulatedImage && msg.voice === true && !msg.type) {
      individualMessage.messageType = 'voice_message'
    }

    // 处理群聊中的特殊消息类型
    if (msg.type) {
      individualMessage.messageType = msg.type
      switch (msg.type) {
        case 'sticker':
          if (msg.stickerName) {
            individualMessage.stickerName = msg.stickerName
          }
          break
        case 'voice_message':
          // voice_message 标记由后处理器 handleVoiceMessageSend 处理 TTS 生成
          break
        case 'reply':
          if (msg.replyTo || msg.replyContent) {
            // replyTo 是被回复者的昵称（群聊/私聊均如此），直接使用
            individualMessage.quote = {
              senderName: msg.replyTo || ctx.characterName,
              content: msg.replyContent || '',
              timestamp: messageTimestamp,
              role: 'user',
            }
          }
          break
      }
    }

    // 图片消息始终保持图片类型，避免兼容 JSON 同时带 voice/type 字段时误触发 TTS。
    if (isSimulatedImage)
      individualMessage.messageType = 'simulated_image'

    // 兜底：只要有 replyTo/replyContent 且 quote 尚未被设置，就转换（兼容私聊和语音+引用等混合场景）
    // replyTo 是被回复者的昵称，直接使用，不做 ID 查找（消息对象无 id 字段）
    if ((msg.replyTo || msg.replyContent) && !individualMessage.quote) {
      individualMessage.quote = {
        senderName: msg.replyTo || ctx.characterName,
        content: msg.replyContent || '',
        timestamp: messageTimestamp,
        role: 'user',
      }
    }

    // 通话模式：给消息添加前缀并标记为隐藏
    if (!isSimulatedImage && ctx.callStore.isCallActive) {
      applyCallModePrefix(individualMessage, ctx.callStore.callType)
      console.info(`💬 通话消息已标记为隐藏: ${msg.text.substring(0, 50)}...`)
    }

    // 处理 emotion 字段（在打字效果之前触发，让表情先变化）
    if (!isSimulatedImage && msg.emotion) {
      const emotionTag = buildEmotionTag(msg.emotion)
      try {
        for (const hook of ctx.onTokenSpecialHooks.value) {
          await hook(emotionTag)
        }
      }
      catch (emotionError) {
        console.warn('情感处理失败，继续', emotionError)
      }
    }

    // Push 消息并等待 textStream 真正播放完毕后清除（多条消息串行，需 await 让气泡逐条出现）
    await pushMessageWithTextStream(individualMessage, textStreamHandle?.done, processedText, ctx, true)

    // 广播完整 AI 消息到其他页面
    broadcastAssistantMessage(individualMessage, ctx)

    // 在发送每条消息后添加随机延迟（最后一条不需要）
    if (i < messagesArray.length - 1) {
      // 显示 typing 指示器，让用户知道还有下一条消息
      ctx.setAiTypingState(true)
      const delay = getRandomDelay(MULTI_MESSAGE_DELAY_MIN_MS, MULTI_MESSAGE_DELAY_MAX_MS)
      await sleep(delay)
      ctx.setAiTypingState(false)
    }
  }

  // 确保 typing 状态被取消
  if (!hasShownFirstMessage) {
    ctx.setAiTypingState(false)
  }

  console.debug('✅ 所有消息发送完毕')

  // 发送桌面通知
  if (messagesArray.length > 0) {
    const firstMsg = messagesArray[0]
    let notificationName = ctx.characterName
    let notificationAvatar: string | undefined

    if (ctx.isGroupChat && firstMsg.senderId) {
      const senderContact = ctx.contactsStore.getContact(firstMsg.senderId)
      if (senderContact) {
        notificationName = senderContact.name
        notificationAvatar = senderContact.avatar
      }
    }
    else {
      // 🔧 使用目标联系人头像，防止切换联系人后通知显示错误头像
      const targetContact = ctx.targetContactId
        ? ctx.contactsStore.getContact(ctx.targetContactId)
        : ctx.contactsStore.activeContact
      notificationAvatar = targetContact?.avatar
    }

    ctx.sendDesktopNotification(
      {
        content: firstMsg.text || (firstMsg.simulatedImages?.length ? `[图片] ${firstMsg.simulatedImages[0].description}` : ''),
        type: firstMsg.simulatedImages?.length ? 'simulated_image' : 'text',
        slices: [{ type: 'text', text: firstMsg.text || '' }],
      },
      {
        skipUserMessage: ctx.options.skipUserMessage,
        characterName: notificationName,
        characterAvatar: notificationAvatar,
      },
    )
  }
}

// ============================================================
// 3. 单条消息处理
// ============================================================

/**
 * 处理单条消息模式（流式消息最终化）
 */
export async function dispatchSingleMessage(
  parsed: ParsedAIResponse,
  ctx: MessageDispatchContext,
): Promise<void> {
  // 如果 slices 为空但 parsed.text 有内容，手动构建 slices
  if (ctx.streamingMessage.value.slices.length === 0 && parsed.text && parsed.text.trim()) {
    ctx.streamingMessage.value.content = parsed.text
    ctx.streamingMessage.value.slices.push({
      type: 'text',
      text: parsed.text,
    })
  }

  const finalMessage = toRaw(ctx.streamingMessage.value)
  finalMessage.timestamp = Date.now()

  // 内心独白：单条路径也需要挂载（多条路径已在 dispatchMultipleMessages 处理）
  // native tool 路径通常走多条消息；这里保留普通文本旁路的统一分发。
  if (parsed.innerVoice?.trim())
    finalMessage.innerVoice = parsed.innerVoice.trim()

  // 通话模式：给消息添加前缀并标记为隐藏
  if (ctx.callStore.isCallActive) {
    const originalContent = typeof finalMessage.content === 'string' ? finalMessage.content : ''
    applyCallModePrefix(finalMessage, ctx.callStore.callType)
    console.info(`💬 通话消息已标记为隐藏: ${originalContent.substring(0, 50)}...`)
  }

  // 调用插件的 onBeforeAIReply 钩子
  const messageContent = typeof finalMessage.content === 'string' ? finalMessage.content : ''
  const processedContent = await runBeforeAIReplyHooks(messageContent, finalMessage.timestamp, ctx)
  // 同步插件改写后的内容回消息对象
  if (processedContent !== messageContent) {
    finalMessage.content = processedContent
    if (finalMessage.slices.length > 0 && finalMessage.slices[0].type === 'text') {
      finalMessage.slices[0].text = processedContent
    }
  }

  // 插件处理后检查消息是否为空（如余额不足时标签被清空）
  if (!processedContent || !processedContent.trim()) {
    console.warn('⚠️ 插件处理后单条消息为空，跳过渲染气泡')
    // 插入一条隐藏的 assistant 消息，让 AI 能感知到操作结果（如余额不足）
    insertHiddenNotificationMessage(Date.now(), ctx)
    ctx.setAiTypingState(false)
    ctx.notifyMessagesChanged()
    return
  }

  ctx.pushMessageToTarget(finalMessage)

  // 广播 AI 消息到其他页面
  broadcastAssistantMessage(finalMessage, ctx)

  // 发送桌面通知
  const contentValue = processedContent || (typeof finalMessage.content === 'string' ? finalMessage.content : '')
  ctx.sendDesktopNotification(
    {
      content: contentValue,
      type: finalMessage.type,
      slices: finalMessage.slices,
    },
    {
      skipUserMessage: ctx.options.skipUserMessage,
      characterName: ctx.characterName,
      characterAvatar: ctx.targetContactId
        ? ctx.contactsStore.getContact(ctx.targetContactId)?.avatar
        : ctx.contactsStore.activeContact?.avatar,
    },
  )
}

// ============================================================
// 4. 空响应兜底
// ============================================================

/**
 * 处理 AI 返回完全空的响应（通常是内容审核过滤导致）
 */
export function dispatchFallbackMessage(ctx: MessageDispatchContext, fullText?: string): void {
  console.warn('⚠️ AI返回空响应（可能被内容审核过滤），插入系统通知')
  const hasContent = fullText && fullText.trim()
  const message = hasContent
    ? 'AI 响应解析失败，无法渲染回复内容（可能是回复格式异常或模型不兼容）'
    : 'AI 返回空响应，请检查 API 服务商或检查提示词是否包含服务商违禁内容'
  const details = hasContent ? `原始响应内容：\n${fullText}` : undefined
  insertSystemNotification(message, 'i-solar:danger-triangle-bold-duotone', ctx, details)
}
